import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useFeesContext } from 'sonata-platform-components';
import { type ValidationErrors, validationErrorChecker } from 'sonata-react-components';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { getInsertionOrderDraftSchema, getInsertionOrderSchema } from '@/validators/entities.validator';
import { reconcileFieldError } from '@/validators/formValidation.helper';

import type { Layer } from 'sonata-static-storage/lib/common/common.types';
import type { InsertionOrder } from 'sonata-static-storage/lib/entities';
import type { ContextualBiddingInterface } from 'sonata-static-storage/lib/schemas/campaign/contextualBidding.schema';
import type { OptimizationModelInterface } from 'sonata-static-storage/lib/schemas/campaign/optimizationModel.schema';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';
import type { SchedulePartingInterface } from 'sonata-static-storage/lib/schemas/campaign/scheduleParting.schema';
import type { FrequencyCapping } from 'sonata-static-storage/lib/schemas/multiFlight/frequencyCapping.schema';
import type { InsertionOrderFlight } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrderFlight.schema';
import type { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';
import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';
import type { ListType } from '@/components/common/MultiFlightMap/multiFlightMap.types';
import type { FormSection, FormSubsection } from '@/types/formSections.types';

type Errors = {
  [key: string]: Errors;
} & ValidationErrors;

type FormState = {
  activeSubsection: string | null;
  selectedSection: FormSection | null;
};

type IOState = {
  ads?: InsertionOrder['ads'];
  adsDistribution?: InsertionOrder['adsDistribution'];
  adsPriceEnabled?: InsertionOrder['adsPriceEnabled'];
  contextualBidding?: ContextualBiddingInterface;
  countries?: string[];
  dco?: InsertionOrder['dco'];
  errors: Errors;
  flights?: InsertionOrderFlight[];
  hasAttemptedValidation: boolean;
  ioNumber?: string;
  isPendingToCreate: boolean;
  isSnliActive: boolean;
  list: ListType;
  mediaType?: InsertionOrder['mediaType'];
  name?: string;
  optimizationModel?: OptimizationModelInterface;
  pricingModelConfig?: PricingModelConfig;
  scheduleParting?: SchedulePartingInterface;
  screenGroupsLayer?: Layer | null;
  screenInventoryExportIds: string[];
  screens?: GeoDataAndScreensInteface[];
  selectedSection: FormSection | null;
  targeting?: TargetingBaseType;
  type?: InsertionOrder['type'];
  userFrequencyCapping?: FrequencyCapping;
  zones?: Zone[];
};

type Context = {
  addScreenInventoryExportId: (exportId: string) => void;
  getFormErrors: () => ValidationErrors;
  getScreens: () => GeoDataAndScreensInteface[] | null;
  getZones: () => Zone[] | null;
  shouldShowValidationErrors: () => boolean;
  setActiveSubsection: (activeSubsection: FormSubsection | null) => void;
  setAds: (ads: InsertionOrder['ads']) => void;
  setAdsDistribution: (newValue: InsertionOrder['adsDistribution']) => void;
  setAdsPriceEnabled: (newValue: InsertionOrder['adsPriceEnabled']) => void;
  setCountries: (countries: string[]) => void;
  setDco: (dco: InsertionOrder['dco']) => void;
  setFlights: (ioNumber: InsertionOrderFlight[]) => void;
  setIoNumber: (ioNumber: string) => void;
  setList: (list: ListType) => void;
  setMediaChannel: (type: InsertionOrder['type']) => void;
  setMediaType: (mediaType: InsertionOrder['mediaType']) => void;
  setName: (name: string) => void;
  setOptimizationModel: (optimizationModel: OptimizationModelInterface) => void;
  setPricingModelConfig: (pricingModelConfig: PricingModelConfig) => void;
  setScheduleParting: (scheduleParting: SchedulePartingInterface) => void;
  setScreenGroupsLayer: (layer?: Layer | null) => void;
  setScreens: (screens: GeoDataAndScreensInteface[]) => void;
  setSelectedSection: (section: FormSection | null) => void;
  setIsSnliActive: (active: boolean) => void;
  setTargeting: (targeting: TargetingBaseType) => void;
  setUserFrequencyCapping: (frequencyCapping: FrequencyCapping) => void;
  setZones: (zones: Zone[]) => void;
  validateForm: (draft?: boolean) => ValidationErrors;
} & FormState &
  IOState;

type Props = {
  defaultCountries?: string[];
  defaultInsertionOrder?: InsertionOrder;
};

const InsertionOrderFormContext = createContext<Context | null>(null);

export function InsertionOrderFormProvider({
  children,
  defaultCountries,
  defaultInsertionOrder
}: PropsWithChildren<Props>) {
  const { formatMessage } = useIntl();
  const { minimumBidPrices } = useFeesContext();

  const [state, setState] = useState<IOState & FormState>({
    countries: defaultCountries,
    ...defaultInsertionOrder,
    activeSubsection: 'mediaChannel',
    errors: {},
    hasAttemptedValidation: false,
    isPendingToCreate: !defaultInsertionOrder?.completed,
    isSnliActive: false,
    list: defaultInsertionOrder?.type === CampaignFamilyEnum.dooh ? 'screens' : 'locations',
    screenGroupsLayer: null,
    screenInventoryExportIds: [],
    selectedSection: null
  });

  const validator = getInsertionOrderSchema(formatMessage, {
    allZonesRetargeting:
      Array.isArray(state.zones) && state.zones.length > 0 && state.zones.every(z => z.retargetingType === 'only'),
    excludeAdExchanges: state.type === CampaignFamilyEnum.dooh,
    excludeAds: !state.ads?.length,
    isCompleted: !!defaultInsertionOrder?.completed,
    minimumBidPrices,
    type: defaultInsertionOrder?.type
  });

  const setValue = (key: keyof IOState, value: any, withValidation = false) => {
    setState(prev => {
      const newState = {
        ...prev,
        [key]: value
      };
      if (withValidation) {
        const fieldValidation = validationErrorChecker(newState, validator)?.[key];
        if (!fieldValidation) {
          delete prev.errors[key];
        }
        return {
          ...newState,
          errors: {
            ...prev.errors,
            ...(fieldValidation && { [key]: fieldValidation })
          }
        };
      }
      return newState;
    });
  };

  const setSelectedSection = (selectedSection: FormSection | null): void => {
    setState(prev => ({
      ...prev,
      selectedSection
    }));
  };

  const setActiveSubsection = (activeSubsection: FormSubsection | null): void => {
    setState(prev => ({
      ...prev,
      activeSubsection
    }));
  };

  const setList = (list: ListType): void => {
    setState(prev => ({
      ...prev,
      list
    }));
  };

  // The media channel can arrive after mount (e.g. IO create applies it on first render), so keep the map list in sync.
  useEffect(() => {
    if (state.type) {
      setList(state.type === CampaignFamilyEnum.dooh ? 'screens' : 'locations');
    }
  }, [state.type]);

  const setAds = (ads: InsertionOrder['ads']): void => {
    setState(prev => {
      const newState = {
        ...prev,
        ads
      };

      const freshValidator = getInsertionOrderSchema(formatMessage, {
        excludeAdExchanges: newState.type === CampaignFamilyEnum.dooh,
        excludeAds: !newState.ads?.length,
        isCompleted: !!defaultInsertionOrder?.completed,
        minimumBidPrices,
        type: defaultInsertionOrder?.type
      });
      return reconcileFieldError(newState, 'ads', freshValidator);
    });
  };

  const setDco = (dco: InsertionOrder['dco']): void => {
    setState(prev => ({
      ...prev,
      dco
    }));
  };

  const setZones = (zones: Zone[]): void => {
    setState(prev => ({
      ...prev,
      zones
    }));
  };

  const addScreenInventoryExportId = (exportId: string) => {
    setState(prev => ({
      ...prev,
      screenInventoryExportIds: [...prev.screenInventoryExportIds, exportId]
    }));
  };

  const setScreenGroupsLayer = (layer?: Layer | null) => {
    setState(prev => ({
      ...prev,
      screenGroupsLayer: layer || null
    }));
  };

  const setScreens = (screens: GeoDataAndScreensInteface[]): void => {
    setState(prev => ({
      ...prev,
      screens
    }));
  };

  const buildFormValidator = (draft: boolean) => {
    const hasSetupLocations = state.zones?.length;
    return draft
      ? getInsertionOrderDraftSchema(formatMessage, {
          excludeAdExchanges: state.type === CampaignFamilyEnum.dooh,
          excludeAds: !state.ads?.length,
          isCompleted: !!defaultInsertionOrder?.completed,
          minimumBidPrices,
          type: defaultInsertionOrder?.type
        })
      : getInsertionOrderSchema(formatMessage, {
          ...(hasSetupLocations && {
            allZonesRetargeting:
              Array.isArray(state.zones) &&
              state.zones.length > 0 &&
              state.zones.every(z => z.retargetingType === 'only')
          }),
          excludeAdExchanges: state.type === CampaignFamilyEnum.dooh,
          excludeAds: !state.ads?.length,
          isCompleted: !!defaultInsertionOrder?.completed,
          minimumBidPrices,
          type: defaultInsertionOrder?.type
        });
  };

  const getFormErrors: Context['getFormErrors'] = () => {
    return validationErrorChecker(state, buildFormValidator(state.isPendingToCreate));
  };

  const getZones = (): Zone[] | null => {
    return state.zones || null;
  };

  const getScreens = (): GeoDataAndScreensInteface[] | null => {
    return state.screens || null;
  };

  const validateForm = (draft = false): ValidationErrors => {
    const errors = validationErrorChecker(state, buildFormValidator(draft)) || {};
    setState(prev => {
      return {
        ...prev,
        errors,
        hasAttemptedValidation: true
      };
    });
    return errors;
  };

  const shouldShowValidationErrors: Context['shouldShowValidationErrors'] = () => {
    return state.hasAttemptedValidation;
  };

  const providerValue = useMemo(
    () => ({
      ...state,
      addScreenInventoryExportId,
      getFormErrors,
      getScreens,
      getZones,
      setActiveSubsection,
      setAds,
      setAdsDistribution: (value: Context['adsDistribution']) => setValue('adsDistribution', value),
      setAdsPriceEnabled: (value: Context['adsPriceEnabled']) => setValue('adsPriceEnabled', value),
      setContextualBidding: (value: Context['contextualBidding']) => setValue('contextualBidding', value),
      setCountries: (value: Context['countries']) => setValue('countries', value),
      setDco,
      setFlights: (value: Context['flights']) => setValue('flights', value),
      setIoNumber: (value: Context['ioNumber']) => setValue('ioNumber', value),
      setIsSnliActive: (value: Context['isSnliActive']) => setValue('isSnliActive', value),
      setList,
      setMediaChannel: (value: Context['type']) => setValue('type', value, true),
      setMediaType: (value: Context['mediaType']) => setValue('mediaType', value),
      setName: (value: Context['name']) => setValue('name', value),
      setOptimizationModel: (value: Context['optimizationModel']) => setValue('optimizationModel', value),
      setPricingModelConfig: (value: Context['pricingModelConfig']) => setValue('pricingModelConfig', value),
      setScheduleParting: (value: Context['scheduleParting']) => setValue('scheduleParting', value),
      setScreenGroupsLayer,
      setScreens,
      setSelectedSection,
      setTargeting: (value: Context['targeting']) => setValue('targeting', value),
      setUserFrequencyCapping: (value: Context['userFrequencyCapping']) => setValue('userFrequencyCapping', value),
      setZones,
      shouldShowValidationErrors,
      validateForm
    }),
    [state]
  );

  return <InsertionOrderFormContext.Provider value={providerValue}>{children}</InsertionOrderFormContext.Provider>;
}

export function useInsertionOrderFormContext(): Context {
  const context = useContext(InsertionOrderFormContext);
  if (!context) {
    throw new Error(
      'The hook useInsertionOrderFormContext cannot be used outside the InsertionOrderFormProvider component'
    );
  }

  return context;
}
